import { useEffect, useState } from "react";
import { versionApi, type VersionView } from "../api/version";

/**
 * Which version of the application is in front of the user (Strahinja,
 * 2026-10-09). Two builds are involved: the frontend bundle (identity baked in
 * at build time) and the server (`GET /api/version`). One deploy builds both
 * from the same commit, so they normally match; when they do not, the browser
 * is showing an older, cached bundle and a refresh is due.
 */
export const FRONTEND_VERSION: VersionView = {
  version: __APP_VERSION__,
  buildSha: __APP_BUILD_SHA__,
  buildTime: null,
};

/** "v1.1.0 (a1b2c3d)"; a local build reads "dev (local)". */
export function formatVersion(v: Pick<VersionView, "version" | "buildSha">): string {
  const label = v.version === "dev" ? "dev" : `v${v.version}`;
  return `${label} (${v.buildSha})`;
}

/** The browser shows an older bundle than the server runs — the two commits differ. */
export function buildsDiffer(frontend: Pick<VersionView, "buildSha">, backend: Pick<VersionView, "buildSha"> | null): boolean {
  return backend !== null && backend.buildSha !== frontend.buildSha;
}

let cache: VersionView | null = null;
let inflight: Promise<VersionView> | null = null;

/** Test hook: drop the module cache so the next mount fetches again. */
export function resetVersionCache(): void {
  cache = null;
  inflight = null;
}

/**
 * The server's build, fetched once per page load and shared by every caller
 * (sidebar, login page, profile). Null until it arrives — and stays null when
 * the server cannot be reached: the version is informational, never an error.
 */
export function useBackendVersion(): VersionView | null {
  const [value, setValue] = useState<VersionView | null>(cache);

  useEffect(() => {
    if (cache) { setValue(cache); return; }
    let cancelled = false;
    if (!inflight) {
      inflight = versionApi.get().then((v) => { cache = v; return v; }).finally(() => { inflight = null; });
    }
    inflight.then((v) => { if (!cancelled) setValue(v); }).catch(() => { /* informational only */ });
    return () => { cancelled = true; };
  }, []);

  return value;
}
